import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { stars } from "@/mocks/stars";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import MovieCard from "@/components/feature/MovieCard";

function highlight(text: string, keyword: string): ReactNode {
  if (!keyword) return text;
  const idx = text.toLowerCase().indexOf(keyword.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-accent-200 text-foreground-950">{text.slice(idx, idx + keyword.length)}</mark>
      {text.slice(idx + keyword.length)}
    </>
  );
}

const resultTabs = ["全部", "电影", "电视剧", "网络剧", "短片", "明星"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [tab, setTab] = useState("全部");
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
    setTab("全部");
    document.title = keyword ? `${keyword} - 激情视频搜索结果` : "搜索 - 激情视频";
  }, [keyword]);

  const matchedMovies = useMemo(
    () =>
      keyword
        ? movies.filter(
            (m) =>
              m.title.includes(keyword) ||
              m.cast.some((c) => c.includes(keyword)) ||
              m.genres.some((g) => g.includes(keyword)),
          )
        : [],
    [keyword],
  );

  const matchedStars = useMemo(
    () => (keyword ? stars.filter((s) => s.name.includes(keyword)) : []),
    [keyword],
  );

  const filteredMovies = useMemo(() => {
    if (tab === "全部" || tab === "明星") return matchedMovies;
    return matchedMovies.filter((m) => m.type === tab);
  }, [matchedMovies, tab]);

  const showStars = tab === "全部" || tab === "明星";
  const total = filteredMovies.length + (showStars ? matchedStars.length : 0);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  const hotKeywords = ["烈火之心", "悬疑", "林述", "都市", "暗夜玫瑰"];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "搜索结果" }]} />

      <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex h-12 flex-1 items-center rounded-md border border-background-200 bg-background-50 focus-within:border-primary-400">
          <i className="ri-search-line ml-4 text-foreground-500" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影视名称、明星或类型关键词"
            className="h-full w-full bg-transparent px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <span>热门搜索：</span>
        {hotKeywords.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setSearchParams({ q: k })}
            className="rounded-md border border-background-200 bg-background-100 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
          >
            {k}
          </button>
        ))}
      </div>

      {keyword ? (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
            <p className="text-sm text-foreground-600">
              关键词 “<span className="font-semibold text-primary-600">{keyword}</span>” 共找到{" "}
              <span className="font-semibold text-primary-600">{total}</span> 条结果
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {resultTabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                  tab === t
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {total > 0 ? (
            <div className="mt-6 space-y-8">
              {filteredMovies.length > 0 && (
                <section>
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
                    <i className="ri-film-line text-primary-500" />
                    影视作品（{filteredMovies.length}）
                  </h2>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {filteredMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-foreground-500">
                    正在浏览包含 “{highlight(keyword, keyword)}” 的作品
                  </p>
                </section>
              )}

              {showStars && matchedStars.length > 0 && (
                <section>
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
                    <i className="ri-user-star-line text-primary-500" />
                    明星（{matchedStars.length}）
                  </h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {matchedStars.map((star) => (
                      <Link
                        key={star.id}
                        to="/stars"
                        className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 cursor-pointer"
                      >
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-background-200">
                          <img
                            src={star.avatar}
                            alt={`${star.name}头像`}
                            title={`${star.name} - 激情视频`}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-base font-bold text-foreground-950">
                            {highlight(star.name, keyword)}
                          </h3>
                          <p className="mt-0.5 text-sm text-foreground-500">{star.role}</p>
                          <p className="mt-1 truncate text-xs text-foreground-400">
                            {star.works.join(" / ")}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div className="mt-6">
              <EmptyState
                title={`没有找到与“${keyword}”相关的作品`}
                desc="试试更换关键词，或浏览下方为你推荐的热门作品。"
              />
              <section className="mt-10">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
                  <i className="ri-fire-line text-primary-500" />
                  为你推荐
                </h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {[...movies]
                    .sort((a, b) => b.popularity - a.popularity)
                    .slice(0, 4)
                    .map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                </div>
              </section>
            </div>
          )}
        </>
      ) : (
        <div className="mt-10">
          <EmptyState title="输入关键词开始搜索" desc="可以搜索影视名称、明星姓名或题材类型，例如“烈火之心”。" />
        </div>
      )}
    </div>
  );
}